import { ChatBubbleOvalLeftEllipsisIcon, FunnelIcon, MinusIcon, PlusIcon, QueueListIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChatBubbleOvalLeftEllipsisIcon as ChatBubbleOvalLeftEllipsisIconSolid } from "@heroicons/react/24/solid";
import pako from "pako";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import VideoPlayer from "../components/video/VideoPlayer";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { addPlaylistItemComment, fetchMatchWithData, fetchUserPlaylists, saveMatch, saveUserPlaylist } from "../utils/dbutils";
import {
  kKeypoint,
  kRally,
  kRallyOutcomeForcingError,
  kRallyOutcomeNetted,
  kRallyOutcomeOutPassingShot,
  kRallyOutcomePassingShot,
  kRallyOutcomePutAway,
  kRallyOutcomeUnforcedError,
  kRallyOutcomeWinner,
  kReturnForcingError,
  kReturning,
  kReturnUnforcedError,
  kReturnWinner,
  kServeAce,
  kServeFootfault,
  kServeNet,
  kServeOut,
  kServeSecond,
  kServeWinner,
  kServing,
} from "../utils/pstsDefines";
import { getClipLeadTime } from "../utils/utils";

const MIN_PANEL = 160;
const MAX_PANEL = 520;
const DEFAULT_PANEL = 240;

// ── Event key ─────────────────────────────────────────────────────────────────
// matchGuid:videoPos:setNumber — uniquely identifies a tennis event across playlists
function makeEventKey(matchGuid, event) {
  const vpos = event.videoTime != null
    ? (Math.round(event.videoTime * 1000) / 1000).toString()
    : "null";
  return `${matchGuid}:${vpos}:${event.setNumber ?? 0}`;
}

// Ordered palette — full Tailwind class strings so JIT can detect them
const PLAYLIST_COLORS = [
  { text: "text-emerald-500", border: "border-l-emerald-500" },
  { text: "text-sky-500",     border: "border-l-sky-500" },
  { text: "text-violet-500",  border: "border-l-violet-500" },
  { text: "text-amber-500",   border: "border-l-amber-500" },
  { text: "text-rose-500",    border: "border-l-rose-500" },
  { text: "text-cyan-500",    border: "border-l-cyan-500" },
];

// savedEventsMap: eventKey → [{ firestoreId, playlistName, colorIdx, psplIdx, comments }]
function buildSavedEventsMap(pls) {
  const sorted = [...pls].sort((a, b) => a.firestoreId.localeCompare(b.firestoreId));
  const colorMap = Object.fromEntries(
    sorted.map((pl, i) => [pl.firestoreId, i % PLAYLIST_COLORS.length])
  );
  const map = {};
  for (const pl of pls) {
    const keys = pl.eventKeys ?? {};
    const plComments = pl.comments ?? {};
    const colorIdx = colorMap[pl.firestoreId] ?? 0;
    for (const [psplIdx, key] of Object.entries(keys)) {
      if (!map[key]) map[key] = [];
      if (!map[key].some((e) => e.firestoreId === pl.firestoreId))
        map[key].push({
          firestoreId: pl.firestoreId,
          playlistName: pl.name,
          colorIdx,
          psplIdx,
          comments: plComments[psplIdx] ?? [],
        });
    }
  }
  return map;
}

// ── PSPL helpers ──────────────────────────────────────────────────────────────

function compressToPspl(str) {
  const compressed = pako.deflate(str);
  let binary = "";
  for (let i = 0; i < compressed.length; i++)
    binary += String.fromCharCode(compressed[i]);
  return btoa(binary);
}

export function decompressPspl(b64) {
  const binary = atob(b64.trim());
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return pako.inflate(bytes, { to: "string" });
}

export function exportPsplFile(playlist) {
  const blob = new Blob([playlist.data], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${playlist.name}.pspl`;
  a.click();
  URL.revokeObjectURL(url);
}

function getResultBadge(e) {
  const { skill, outcome, subskill1 } = e;
  if (skill === kServing) {
    if (outcome === kServeAce) return { label: "Ace", color: "badge-success" };
    if (outcome === kServeWinner) return { label: "Srv Winner", color: "badge-success" };
    const isSecond = subskill1 === kServeSecond;
    if (isSecond && (outcome === kServeOut || outcome === kServeNet || outcome === kServeFootfault))
      return { label: "Double Fault", color: "badge-error" };
  } else if (skill === kReturning) {
    if (outcome === kReturnWinner) return { label: "Winner", color: "badge-success" };
    if (outcome === kReturnForcingError) return { label: "Forced Error", color: "badge-warning" };
    if (outcome === kReturnUnforcedError) return { label: "Unforced Error", color: "badge-error" };
  } else if (skill === kRally || skill === kKeypoint) {
    if (outcome === kRallyOutcomeWinner) return { label: "Winner", color: "badge-success" };
    if (outcome === kRallyOutcomePassingShot) return { label: "Passing Winner", color: "badge-success" };
    if (outcome === kRallyOutcomeOutPassingShot) return { label: "Passing Winner", color: "badge-error" };
    if (outcome === kRallyOutcomePutAway) return { label: "Put Away", color: "badge-success" };
    if (outcome === kRallyOutcomeForcingError) return { label: "Forced Error", color: "badge-error" };
    if (outcome === kRallyOutcomeUnforcedError) return { label: "Unforced Error", color: "badge-error" };
    if (outcome === kRallyOutcomeNetted) return { label: "Netted", color: "badge-error" };
  }
  return null;
}

function eventDescription(event, badge) {
  if (!badge || !event.eventString) return event.eventString;
  const lastDash = event.eventString.lastIndexOf(" - ");
  return lastDash !== -1 ? event.eventString.substring(0, lastDash) : event.eventString;
}

function parseScores(realScores) {
  if (!realScores) return { gameScore: "", setScore: "" };
  const parts = realScores.split(" ");
  return { setScore: parts[0] ?? "", gameScore: parts[1] ?? "" };
}

function EventRow({ event, matchGuid, isSelected, onClick, isInPlaylist, onTogglePlaylist, hasComments, onOpenChat, savedEntries }) {
  const playerName = event.player
    ? `${event.player.firstName ?? ""} ${event.player.lastName ?? ""}`.trim()
    : "Unknown";

  const { gameScore, setScore } = parseScores(event.realScores);
  const badge = getResultBadge(event);
  const setInfo = `(${event.setNumber ?? ""}) ${setScore.replace(":", "-")}`.trim();

  // Saved-in-playlist indicator color
  const savedColor =
    savedEntries?.length === 1
      ? PLAYLIST_COLORS[savedEntries[0].colorIdx].text
      : savedEntries?.length > 1
      ? "text-fuchsia-500"
      : null;

  return (
    <div
      className={`px-3 py-2 border-b border-base-200 hover:bg-base-200 transition-colors cursor-pointer${
        isSelected ? " bg-primary/10" : ""
      }`}
      onClick={onClick}
    >
      {/* Line 1: player name + chat icon + playlist toggle */}
      <div className="flex justify-between items-baseline gap-1">
        <span className="text-xs font-semibold truncate leading-tight">{playerName}</span>
        <div className="flex items-center gap-0.5 shrink-0">
          {/* Saved-in-playlist dot(s) */}
          {savedColor && !isInPlaylist && (
            <span
              className={`w-2 h-2 rounded-full ${savedColor} inline-block`}
              title={savedEntries.map((e) => e.playlistName).join(", ")}
              style={{ background: "currentColor" }}
            />
          )}
          <button
            className={`p-0.5 rounded hover:bg-base-300 transition-colors ${
              hasComments ? "text-primary" : "text-base-content/25 hover:text-base-content/60"
            }`}
            onClick={(e) => { e.stopPropagation(); onOpenChat(event); }}
            title={hasComments ? "View/add comments" : "Add comment"}
          >
            {hasComments ? (
              <ChatBubbleOvalLeftEllipsisIconSolid className="w-3 h-3" />
            ) : (
              <ChatBubbleOvalLeftEllipsisIcon className="w-3 h-3" />
            )}
          </button>
          <button
            className={`p-0.5 rounded hover:bg-base-300 transition-colors ${
              isInPlaylist ? "text-success" : "text-base-content/40"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onTogglePlaylist(event);
            }}
            title={isInPlaylist ? "Remove from playlist" : "Add to playlist"}
          >
            {isInPlaylist ? (
              <MinusIcon className="w-3 h-3" />
            ) : (
              <PlusIcon className="w-3 h-3" />
            )}
          </button>
        </div>
      </div>

      {/* Line 2: event description without outcome + game score */}
      <div className="flex justify-between items-baseline mt-0.5 gap-1">
        <span className="text-xs text-base-content/80 truncate leading-tight">
          {eventDescription(event, badge)}
        </span>
        <span className="text-xs text-base-content/40 tabular-nums shrink-0">{gameScore}</span>
      </div>

      {/* Line 3: result badge left + set info right */}
      <div className="flex mt-0.5">
        {badge && (
          <span className={`badge badge-sm shrink-0 ${badge.color}`}>{badge.label}</span>
        )}
        <div className="flex-1" />
        <span className="text-xs text-base-content/40 tabular-nums">{setInfo}</span>
      </div>
    </div>
  );
}

function VideoAnalysis({ embeddedMode = false, externalMatchGuids, externalFilteredIds, onClose } = {}) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const matchGuids = externalMatchGuids ?? state?.matchGuids ?? [];
  const playerGuid = state?.playerGuid ?? null;
  const filteredEventIds = useMemo(
    () => externalFilteredIds !== undefined
      ? externalFilteredIds
      : (state?.filteredEventIds ? new Set(state.filteredEventIds) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [externalFilteredIds]
  ); // null means "show all"
  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;

  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [playlist, setPlaylist] = useState(new Set()); // Set<eventKey string>
  const [videoPlayerRef, setVideoPlayerRef] = useState(null);

  // Saved-events map: eventKey → [{ firestoreId, playlistName, colorIdx }]
  const [savedEventsMap, setSavedEventsMap] = useState({});

  // Playlist panel state
  const [showPlaylistPanel, setShowPlaylistPanel] = useState(false);
  const [playlistName, setPlaylistName] = useState("");
  const [appendTarget, setAppendTarget] = useState(null);
  const [existingPlaylists, setExistingPlaylists] = useState([]);
  const [playlistSaving, setPlaylistSaving] = useState(false);
  const [playlistError, setPlaylistError] = useState("");

  // Comment / chat state
  const [pendingComments, setPendingComments] = useState({}); // { [eventKey]: [{text,author,ts}] }
  const [chatEventKey, setChatEventKey] = useState(null);
  const [chatEvent, setChatEvent] = useState(null);
  const [chatDraft, setChatDraft] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const chatInputRef = useRef(null);
  const chatBottomRef = useRef(null);
  const commentAuthorName = firebaseUser?.displayName || firebaseUser?.email || "Me";

  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(DEFAULT_PANEL);

  // On a portrait mobile screen, stack the video on top and the event list
  // below (instead of the side-by-side layout).
  const [stackVertical, setStackVertical] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px) and (orientation: portrait)");
    const update = () => setStackVertical(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const onMouseDown = useCallback(
    (e) => {
      dragging.current = true;
      startX.current = e.clientX;
      startWidth.current = panelWidth;
      e.preventDefault();
    },
    [panelWidth]
  );

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const next = Math.min(
        MAX_PANEL,
        Math.max(MIN_PANEL, startWidth.current + (e.clientX - startX.current))
      );
      setPanelWidth(next);
    };
    const onMouseUp = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (chatEventKey !== null) {
      setChatDraft("");
      setTimeout(() => chatInputRef.current?.focus(), 80);
    }
  }, [chatEventKey]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatEventKey, pendingComments]);

  useEffect(() => {
    if (matchGuids.length === 0 || !uid) return;
    setLoadingMatches(true);
    Promise.all(matchGuids.map((g) => fetchMatchWithData(uid, g)))
      .then((results) => {
        const validMatches = results.filter(Boolean);
        // Tag every event with its match guid for cross-match filtering
        for (const m of validMatches) {
          for (const s of m.sets ?? []) {
            for (const e of s.events ?? []) {
              e._matchGuid = m.guid;
            }
          }
        }
        setMatches(validMatches);
      })
      .finally(() => setLoadingMatches(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Load existing playlists on mount to show saved indicators
  useEffect(() => {
    loadExistingPlaylists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const multiMatch = matchGuids.length > 1;

  // Derive the video source: prefer the selected event's file, then the first match
  const videoSrc =
    selectedEvent?.videoFile ||
    matches[0]?.videoFile ||
    "";

  // Persist a URL-based source to all loaded matches in Firestore and update local state
  const handleUrlSaved = async (url) => {
    const updatedMatches = matches.map((m) => ({
      ...m,
      videoFile: url,
      sets: (m.sets ?? []).map((s) => ({
        ...s,
        events: (s.events ?? []).map((e) => ({ ...e, videoFile: url })),
      })),
    }));
    setMatches(updatedMatches);
    await Promise.all(updatedMatches.map((m) => saveMatch(m, uid)));
  };

  // Playlist management
  // eventKey for in-memory playlist toggling (fast, no match guid needed)
  const eventKey = (event) =>
    `${event.eventId || event.timestamp}_${event.setNumber}`;

  // makeEventKey for cross-session storage (includes matchGuid + videoTime)
  const getMatchGuid = (event) =>
    matches.find((m) => m.sets?.some((s) => s.events?.some((e) => e === event)))?.guid ?? "";

  const getSavedEntries = (event) =>
    savedEventsMap[makeEventKey(getMatchGuid(event), event)] ?? [];

  const handleTogglePlaylist = (event) => {
    setPlaylist((prev) => {
      const next = new Set(prev);
      const k = eventKey(event);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const isEventInPlaylist = (event) => playlist.has(eventKey(event));

  const allVisibleEvents = useMemo(() => {
    const evts = [];
    for (const m of matches) {
      for (const s of m.sets ?? []) {
        for (const e of s.events ?? []) {
          if (filteredEventIds !== null) {
            // eventId is only unique WITHIN a set (reset to 0 per set by
            // pstsFile.js's normaliseCodingSets) — setNumber must be part of
            // the key too, or events from other sets collide with it.
            const key = e._matchGuid ? `${e._matchGuid}_${e.setNumber}_${e.eventId}` : `${e.setNumber}_${e.eventId}`;
            if (!filteredEventIds.has(key)) continue;
          }
          evts.push(e);
        }
      }
    }
    return evts;
  }, [matches, filteredEventIds]);

  const handleAddAllFiltered = () => {
    setPlaylist((prev) => {
      const next = new Set(prev);
      for (const e of allVisibleEvents) next.add(eventKey(e));
      return next;
    });
  };

  const handleRemoveAllFiltered = () => {
    setPlaylist((prev) => {
      const next = new Set(prev);
      for (const e of allVisibleEvents) next.delete(eventKey(e));
      return next;
    });
  };

  const openChat = (event) => {
    setChatEventKey(eventKey(event));
    setChatEvent(event);
  };

  const sendComment = async () => {
    if (!chatDraft.trim() || !chatEventKey) return;
    const msg = { text: chatDraft.trim(), author: commentAuthorName, ts: Date.now() };
    setChatSending(true);
    const savedEntries = chatEvent ? getSavedEntries(chatEvent) : [];
    if (savedEntries.length > 0) {
      // Event is already in a saved playlist — persist comment to Firestore
      const results = await Promise.all(
        savedEntries.map((entry) =>
          addPlaylistItemComment(entry.firestoreId, entry.psplIdx, msg)
        )
      );
      if (results.every((r) => !r.error)) {
        setSavedEventsMap((prev) => ({
          ...prev,
          [chatEventKey]: (prev[chatEventKey] ?? []).map((entry) => ({
            ...entry,
            comments: [...entry.comments, msg],
          })),
        }));
      }
    } else {
      // Not yet saved — store locally; auto-add to current playlist
      setPendingComments((prev) => ({
        ...prev,
        [chatEventKey]: [...(prev[chatEventKey] ?? []), msg],
      }));
      setPlaylist((prev) => {
        if (prev.has(chatEventKey)) return prev;
        const next = new Set(prev);
        next.add(chatEventKey);
        return next;
      });
    }
    setChatDraft("");
    setChatSending(false);
    setTimeout(() => chatInputRef.current?.focus(), 50);
  };

  // Build PSPL base64 string from current playlist
  const buildPspl = (name) => {
    let str = `${name}[0][0]${new Date().toISOString()}\n`;
    for (const match of matches) {
      for (const set of match.sets ?? []) {
        for (const event of set.events ?? []) {
          if (!playlist.has(eventKey(event))) continue;
          const playerName = event.player
            ? `${event.player.firstName ?? ""} ${event.player.lastName ?? ""}`.trim()
            : "Unknown";
          const badge = getResultBadge(event);
          const desc = eventDescription(event, badge) ?? "";
          const videoUrl = match.videoFile ?? "";
          const videoPos = event.videoTime != null ? Math.max(0, event.videoTime - getClipLeadTime()) : 0;
          const resultLabel = badge?.label ?? "";
          const { setScore } = parseScores(event.realScores);
          const matchName =
            [match.player1FirstName, match.player1LastName].filter(Boolean).join(" ") +
            " vs " +
            [match.player2FirstName, match.player2LastName].filter(Boolean).join(" ");
          const subString = `[S${set.number ?? ""} ${setScore.replace(":", "-")}] - ${matchName}`;
          const eventComments = pendingComments[eventKey(event)] ?? [];
          const commentsSegment = eventComments.length > 0
            ? btoa(unescape(encodeURIComponent(JSON.stringify(eventComments))))
            : "";
          str += `${playerName}[0]${desc}[0]${videoUrl}[0]${videoPos}[0]${resultLabel}[0]${subString}[0]${commentsSegment}[0][0][0][0]\n`;
        }
      }
    }
    return compressToPspl(str);
  };

  const loadExistingPlaylists = async () => {
    if (!uid) return;
    const pls = await fetchUserPlaylists(uid);
    setExistingPlaylists(pls);
    setSavedEventsMap(buildSavedEventsMap(pls));
  };

  const handleOpenPlaylistPanel = async () => {
    setPlaylistError("");
    setShowPlaylistPanel(true);
    await loadExistingPlaylists();
  };

  const handleSavePlaylist = async () => {
    if (!uid || !playlistName.trim() || playlist.size === 0) return;
    setPlaylistSaving(true);
    setPlaylistError("");
    const b64 = buildPspl(playlistName.trim());
    // Build comments map and eventKeys map: pspl index → value
    const comments = {};
    const eventKeys = {};
    const itemPlayerGuids = [];
    let psplIdx = 0;
    for (const match of matches) {
      for (const set of match.sets ?? []) {
        for (const event of set.events ?? []) {
          if (!playlist.has(eventKey(event))) continue;
          const k = eventKey(event);
          if (pendingComments[k]?.length) comments[String(psplIdx)] = pendingComments[k];
          eventKeys[String(psplIdx)] = makeEventKey(match.guid, event);
          itemPlayerGuids.push(event.player?.guid ?? null);
          psplIdx++;
        }
      }
    }
    // If every item references the same (defined) player, tag the playlist with that player.
    const firstGuid = itemPlayerGuids[0];
    const singlePlayerGuid =
      firstGuid && itemPlayerGuids.every((g) => g === firstGuid) ? firstGuid : null;
    const result = await saveUserPlaylist(uid, {
      name: playlistName.trim(),
      data: b64,
      items: playlist.size,
      ...(Object.keys(comments).length > 0 ? { comments } : {}),
      eventKeys,
      playerGuid: singlePlayerGuid,
    });
    setPlaylistSaving(false);
    if (result.error) {
      setPlaylistError(result.error);
    } else {
      setPlaylistName("");
      await loadExistingPlaylists();
    }
  };

  const handleAppendToPlaylist = async () => {
    if (!uid || !appendTarget || playlist.size === 0) return;
    const target = existingPlaylists.find((p) => p.firestoreId === appendTarget);
    if (!target) return;
    setPlaylistSaving(true);
    setPlaylistError("");
    try {
      const xstr = decompressPspl(target.data);
      const lines = xstr.split("\n");
      const existingCount = lines.filter((l, i) => i > 0 && l.trim()).length;
      const newLines = [];
      const newComments = {};
      const newEventKeys = {};
      const newItemPlayerGuids = [];
      let newIdx = 0;
      for (const match of matches) {
        for (const set of match.sets ?? []) {
          for (const event of set.events ?? []) {
            if (!playlist.has(eventKey(event))) continue;
            const playerName = event.player
              ? `${event.player.firstName ?? ""} ${event.player.lastName ?? ""}`.trim()
              : "Unknown";
            const badge = getResultBadge(event);
            const desc = eventDescription(event, badge) ?? "";
            const videoUrl = match.videoFile ?? "";
            const videoPos = event.videoTime != null ? Math.max(0, event.videoTime - getClipLeadTime()) : 0;
            const resultLabel = badge?.label ?? "";
            const { setScore } = parseScores(event.realScores);
            const matchName =
              [match.player1FirstName, match.player1LastName].filter(Boolean).join(" ") +
              " vs " +
              [match.player2FirstName, match.player2LastName].filter(Boolean).join(" ");
            const subString = `[S${set.number ?? ""} ${setScore.replace(":", "-")}] - ${matchName}`;
            const posStr = `[0]${videoPos}[0]`;
            const alreadyExists = lines.some(
              (line) => line.includes(subString) && line.includes(posStr)
            );
            if (!alreadyExists) {
              const k = eventKey(event);
              const eventComments = pendingComments[k] ?? [];
              const commentsSegment = eventComments.length > 0
                ? btoa(unescape(encodeURIComponent(JSON.stringify(eventComments))))
                : "";
              newLines.push(
                `${playerName}[0]${desc}[0]${videoUrl}[0]${videoPos}[0]${resultLabel}[0]${subString}[0]${commentsSegment}[0][0][0][0]`
              );
              if (eventComments.length > 0)
                newComments[String(existingCount + newIdx)] = eventComments;
              newEventKeys[String(existingCount + newIdx)] = makeEventKey(match.guid, event);
              newItemPlayerGuids.push(event.player?.guid ?? null);
              newIdx++;
            }
          }
        }
      }
      const merged = [lines[0], ...lines.slice(1).filter((l) => l.trim()), ...newLines].join("\n");
      const b64 = compressToPspl(merged);
      const count = merged.split("\n").filter((l, i) => i > 0 && l.trim()).length;
      const mergedComments = { ...(target.comments ?? {}), ...newComments };
      const mergedEventKeys = { ...(target.eventKeys ?? {}), ...newEventKeys };
      // Player tagging: keep existing playerGuid only if every newly-appended item
      // belongs to that same player. Otherwise clear it.
      const targetPlayerGuid = target.playerGuid ?? null;
      const newAllSame =
        newItemPlayerGuids.length > 0 &&
        newItemPlayerGuids.every((g) => g && g === newItemPlayerGuids[0]);
      let mergedPlayerGuid = null;
      if (targetPlayerGuid) {
        if (newItemPlayerGuids.length === 0) {
          mergedPlayerGuid = targetPlayerGuid;
        } else if (newAllSame && newItemPlayerGuids[0] === targetPlayerGuid) {
          mergedPlayerGuid = targetPlayerGuid;
        }
      } else if (existingCount === 0 && newAllSame) {
        // Existing playlist had no items yet, so the new items define the player.
        mergedPlayerGuid = newItemPlayerGuids[0];
      }
      const result = await saveUserPlaylist(uid, {
        firestoreId: target.firestoreId,
        name: target.name,
        data: b64,
        items: count,
        ...(Object.keys(mergedComments).length > 0 ? { comments: mergedComments } : {}),
        eventKeys: mergedEventKeys,
        playerGuid: mergedPlayerGuid,
      });
      if (result.error) {
        setPlaylistError(result.error);
      } else {
        setAppendTarget(null);
        await loadExistingPlaylists();
      }
    } catch (e) {
      setPlaylistError(e.message);
    }
    setPlaylistSaving(false);
  };

  const handleExportPspl = () => {
    if (playlist.size === 0) return;
    const name = playlistName.trim() || "playlist";
    const b64 = buildPspl(name);
    const blob = new Blob([b64], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.pspl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Sync selected event to current video position
  const handleSyncEvent = async () => {
    if (!selectedEvent || !videoPlayerRef) return;
    
    // Get current video time from the player
    const currentTime = videoPlayerRef.getCurrentTime();
    if (currentTime == null) return;

    // Calculate the offset between video time and selected event timestamp
    const selectedEventTimestamp = selectedEvent.timestamp || 0;
    const videoTimeOffset = currentTime - selectedEventTimestamp;
    
    // Find which match contains the selected event
    const selectedMatchGuid = matches.find(match => 
      match.sets?.some(set => 
        set.events?.some(event => event === selectedEvent)
      )
    )?.guid;

    if (!selectedMatchGuid) return;

    // Compute the updated match directly from current state
    const currentMatch = matches.find((m) => m.guid === selectedMatchGuid);
    const updatedMatch = {
      ...currentMatch,
      sets: currentMatch.sets?.map((set) => ({
        ...set,
        events: set.events?.map((event) => ({
          ...event,
          videoTime: videoTimeOffset + (event.timestamp || 0),
        })),
      })),
    };

    // Update React state
    setMatches((prevMatches) =>
      prevMatches.map((match) => (match.guid !== selectedMatchGuid ? match : updatedMatch))
    );

    // Update the selected event reference
    setSelectedEvent((prev) => ({ ...prev, videoTime: currentTime }));

    // Save the updated match data to database
    try {
      await saveMatch(updatedMatch, uid);
    } catch (error) {
      console.error('Failed to save match video times:', error);
    }
  };

  return (
    <div className={`flex flex-col ${embeddedMode ? "h-full" : "h-[calc(100vh-3rem)]"}`}>
      {/* Header — hidden in embedded mode to save vertical space (close moves
          to a floating button on the video panel). */}
      {!embeddedMode && (
      <div className="flex items-center gap-3 px-4 py-2 border-b border-base-300 shrink-0">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 className="text-xl font-bold">Video Analysis</h1>
        {multiMatch && (
          <span className="badge badge-primary">{matchGuids.length} matches</span>
        )}
        {loadingMatches && <span className="loading loading-spinner loading-sm" />}
      </div>
      )}

      {/* Body */}
      <div className={`flex flex-1 overflow-hidden ${stackVertical ? "flex-col" : ""}`}>
        {/* Left panel — event list */}
        <div
          className={`flex flex-col border-base-300 bg-base-100 overflow-hidden ${
            stackVertical
              ? "order-2 flex-1 min-h-0 w-full border-t"
              : "shrink-0 border-r"
          }`}
          style={stackVertical ? undefined : { width: panelWidth }}
        >
          {/* Toolbar */}
          <div className="shrink-0 flex items-center gap-1 px-2 py-1 border-b border-base-300 bg-base-200">
            <button
              className={`btn btn-xs btn-ghost flex items-center gap-1 ${
                playlist.size > 0 ? "text-primary" : ""
              }`}
              onClick={handleOpenPlaylistPanel}
              title="Playlist"
            >
              <QueueListIcon className="w-3.5 h-3.5" />
              <span>Playlist{playlist.size > 0 ? ` (${playlist.size})` : ""}</span>
            </button>
            <div className="flex-1" />
            <button
              className="btn btn-xs btn-ghost"
              onClick={handleAddAllFiltered}
              title={`Add all ${allVisibleEvents.length} visible events to playlist`}
              disabled={allVisibleEvents.length === 0}
            >
              <PlusIcon className="w-3 h-3" />
            </button>
            <button
              className="btn btn-xs btn-ghost text-error"
              onClick={handleRemoveAllFiltered}
              title="Remove all visible events from playlist"
              disabled={allVisibleEvents.length === 0}
            >
              <MinusIcon className="w-3 h-3" />
            </button>
            {!embeddedMode && (
              <button
                className="btn btn-xs btn-ghost"
                onClick={() => navigate('/filters-analysis', { state: { matchGuids, playerGuid } })}
                title="Filter events"
              >
                <FunnelIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
          {matches.length === 0 && !loadingMatches && (
            <p className="text-center text-base-content/40 py-8 text-sm px-3">
              No match data.
            </p>
          )}

          {matches.map((match) => (
            <div key={match.guid}>
              {/* Match header — only shown when combining multiple matches */}
              {multiMatch && (
                <div className="px-3 py-1.5 bg-base-300 text-xs font-bold text-base-content/70 border-b border-base-300">
                  {[match.player1FirstName, match.player1LastName].filter(Boolean).join(" ")}{" "}
                  vs{" "}
                  {[match.player2FirstName, match.player2LastName].filter(Boolean).join(" ")}
                </div>
              )}

              {(match.sets ?? []).map((set, si) => (
                <div key={set.number ?? si}>
                  {/* Set header */}
                  <div className="px-3 py-1 bg-base-200 text-xs font-semibold text-base-content/50 border-b border-base-200">
                    Set {set.number}
                    {set.playerScore != null && set.oppositionScore != null && (
                      <span className="ml-2 tabular-nums">
                        {set.playerScore}–{set.oppositionScore}
                      </span>
                    )}
                  </div>

                  {(set.events ?? [])
                    .filter((event) => {
                      if (filteredEventIds === null) return true;
                      // Keep in sync with allVisibleEvents' key (matchGuid +
                      // setNumber + eventId — eventId alone repeats per set).
                      const key = event._matchGuid ? `${event._matchGuid}_${event.setNumber}_${event.eventId}` : `${event.setNumber}_${event.eventId}`;
                      return filteredEventIds.has(key);
                    })
                    .map((event, ei) => (
                    <EventRow
                      key={event.eventId ?? ei}
                      event={event}
                      matchGuid={match.guid}
                      isSelected={selectedEvent === event}
                      onClick={() => {
                        setSelectedEvent(event);
                        if (event.videoTime != null && videoPlayerRef && videoPlayerRef.seekTo) {
                          videoPlayerRef.seekTo(Math.max(0, event.videoTime - getClipLeadTime()));
                        }
                      }}
                      isInPlaylist={isEventInPlaylist(event)}
                      onTogglePlaylist={handleTogglePlaylist}
                      hasComments={
                        (pendingComments[eventKey(event)]?.length ?? 0) > 0 ||
                        getSavedEntries(event).some((e) => e.comments.length > 0)
                      }
                      onOpenChat={openChat}
                      savedEntries={getSavedEntries(event)}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}          </div>        </div>

        {/* Drag handle */}
        {!stackVertical && (
        <div
          className="w-1 cursor-col-resize bg-base-300 hover:bg-primary active:bg-primary transition-colors shrink-0"
          onMouseDown={onMouseDown}
        />
        )}

        {/* Right panel — video */}
        <div
          className={`flex flex-col overflow-hidden relative ${
            stackVertical ? "order-1 w-full shrink-0" : "flex-1"
          }`}
        >
          {embeddedMode && (
            <button
              className="btn btn-sm btn-circle btn-neutral absolute top-2 right-2 z-20 shadow"
              onClick={onClose}
              title="Back to court"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
          {selectedEvent && (
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-base-300 bg-base-100 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold truncate">
                  {eventDescription(selectedEvent, getResultBadge(selectedEvent))}
                </span>
                {getResultBadge(selectedEvent) && (
                  <span className={`badge badge-sm ${getResultBadge(selectedEvent).color}`}>
                    {getResultBadge(selectedEvent).label}
                  </span>
                )}
              </div>
            </div>
          )}
          <div className={stackVertical ? "shrink-0" : "flex-1 overflow-hidden"}>
            <VideoPlayer 
              initialSrc={videoSrc} 
              onUrlSaved={handleUrlSaved}
              hideSourceControls={embeddedMode}
              onSync={handleSyncEvent}
              syncDisabled={!selectedEvent}
              fitWidth={stackVertical}
              ref={setVideoPlayerRef}
            />
          </div>
        </div>
      </div>

      {/* Playlist Panel Modal */}
      {showPlaylistPanel && (
        <div className="modal modal-open z-50">
          <div className="modal-box w-full max-w-lg overflow-visible">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">
                Playlist{" "}
                {playlist.size > 0 && (
                  <span className="text-sm font-normal text-base-content/60">
                    ({playlist.size} events)
                  </span>
                )}
              </h3>
              <button
                className="btn btn-sm btn-circle btn-ghost"
                onClick={() => setShowPlaylistPanel(false)}
              >
                ✕
              </button>
            </div>

            {/* Event list preview */}
            {playlist.size === 0 ? (
              <p className="text-sm text-base-content/50 mb-4">
                No events in playlist. Use the + button on events to add them.
              </p>
            ) : (
              <ul className="divide-y divide-base-200 mb-4 max-h-52 overflow-y-auto text-xs">
                {matches.flatMap((match) =>
                  (match.sets ?? []).flatMap((set) =>
                    (set.events ?? [])
                      .filter((event) => isEventInPlaylist(event))
                      .map((event, i) => {
                        const playerName = event.player
                          ? `${event.player.firstName ?? ""} ${event.player.lastName ?? ""}`.trim()
                          : "Unknown";
                        const badge = getResultBadge(event);
                        return (
                          <li
                            key={eventKey(event)}
                            className="flex items-center justify-between py-1 px-1 hover:bg-base-200 cursor-pointer"
                            onClick={() => {
                              setSelectedEvent(event);
                              if (event.videoTime != null && videoPlayerRef?.seekTo)
                                videoPlayerRef.seekTo(Math.max(0, event.videoTime - getClipLeadTime()));
                            }}
                          >
                            <span className="truncate">
                              <strong className="mr-1">{playerName}</strong>
                              {eventDescription(event, badge)}
                              {badge && (
                                <span className={`ml-1 badge badge-xs ${badge.color}`}>
                                  {badge.label}
                                </span>
                              )}
                              <span className="text-base-content/50 ml-1">Set {set.number}</span>
                            </span>
                            <button
                              className="btn btn-xs btn-ghost text-error ml-2 shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTogglePlaylist(event);
                              }}
                            >
                              −
                            </button>
                          </li>
                        );
                      })
                  )
                )}
              </ul>
            )}

            {uid ? (
              <div className="flex flex-col gap-3">
                {/* Save as new */}
                <div>
                  <label className="block text-sm font-semibold mb-1">Save as new playlist</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input input-sm input-bordered flex-1"
                      placeholder="Playlist name…"
                      value={playlistName}
                      onChange={(e) => setPlaylistName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleSavePlaylist(); }}
                    />
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={!playlistName.trim() || playlist.size === 0 || playlistSaving}
                      onClick={handleSavePlaylist}
                    >
                      {playlistSaving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>

                {/* Append to existing */}
                {existingPlaylists.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      Append to existing playlist
                    </label>
                    <div className="flex gap-2">
                      <select
                        className="select select-sm select-bordered flex-1"
                        value={appendTarget ?? ""}
                        onChange={(e) => setAppendTarget(e.target.value || null)}
                      >
                        <option value="">Select playlist…</option>
                        {existingPlaylists.map((pl) => (
                          <option key={pl.firestoreId} value={pl.firestoreId}>
                            {pl.name} ({pl.items ?? 0} events)
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={!appendTarget || playlist.size === 0 || playlistSaving}
                        onClick={handleAppendToPlaylist}
                      >
                        {playlistSaving ? "…" : "Append"}
                      </button>
                    </div>
                  </div>
                )}

                {playlistError && (
                  <p className="text-xs text-error">{playlistError}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-base-content/50">Sign in to save playlists.</p>
            )}

            <div className="modal-action">
              <button
                className="btn btn-outline btn-sm"
                disabled={playlist.size === 0}
                onClick={handleExportPspl}
                title="Export as .pspl file"
              >
                Export .pspl
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowPlaylistPanel(false)}
              >
                Close
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowPlaylistPanel(false)} />
        </div>
      )}

      {/* Chat / Comment Modal */}
      {chatEventKey && chatEvent && (() => {
        const playerName = chatEvent.player
          ? `${chatEvent.player.firstName ?? ""} ${chatEvent.player.lastName ?? ""}`.trim()
          : "Unknown";
        const badge = getResultBadge(chatEvent);
        const savedEntries = getSavedEntries(chatEvent);
        const savedComments = savedEntries.flatMap((e) => e.comments);
        const comments = [
          ...(pendingComments[chatEventKey] ?? []),
          ...savedComments,
        ].sort((a, b) => a.ts - b.ts);
        const isSaved = savedEntries.length > 0;
        const closeChat = () => { setChatEventKey(null); setChatEvent(null); };
        return (
          <div className="modal modal-open z-[60]">
            <div className="modal-box w-full max-w-md flex flex-col" style={{ maxHeight: "80vh" }}>
              {/* Header */}
              <div className="flex items-start justify-between mb-3 shrink-0">
                <div>
                  <p className="font-semibold text-sm">{playerName}</p>
                  <p className="text-xs text-base-content/60">{eventDescription(chatEvent, badge)}</p>
                  {badge && (
                    <span className={`badge badge-xs ${badge.color} mt-0.5`}>{badge.label}</span>
                  )}
                  {isSaved && (
                    <p className="text-xs text-base-content/40 mt-0.5">
                      Saved in: {savedEntries.map((e) => e.playlistName).join(", ")}
                    </p>
                  )}
                </div>
                <button className="btn btn-sm btn-circle btn-ghost" onClick={closeChat}>✕</button>
              </div>

              {/* Comments list */}
              <div className="flex-1 overflow-y-auto min-h-0 mb-3 space-y-2">
                {comments.length === 0 ? (
                  <p className="text-xs text-base-content/40 text-center py-4">
                    {isSaved
                      ? "No comments yet."
                      : "No comments yet — adding one will also add this event to your playlist."}
                  </p>
                ) : (
                  comments.map((c, i) => (
                    <div key={i} className="bg-base-200 rounded-lg px-3 py-2">
                      <div className="flex items-baseline justify-between gap-2 mb-0.5">
                        <span className="text-xs font-semibold">{c.author}</span>
                        <span className="text-xs text-base-content/40">
                          {new Date(c.ts).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm">{c.text}</p>
                    </div>
                  ))
                )}
                <div ref={chatBottomRef} />
              </div>

              {/* Input */}
              <div className="flex gap-2 shrink-0">
                <input
                  ref={chatInputRef}
                  type="text"
                  className="input input-sm input-bordered flex-1"
                  placeholder="Add a comment…"
                  value={chatDraft}
                  onChange={(e) => setChatDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendComment(); }
                  }}
                />
                <button
                  className="btn btn-sm btn-primary"
                  disabled={!chatDraft.trim() || chatSending}
                  onClick={sendComment}
                >
                  {chatSending ? "…" : "Send"}
                </button>
              </div>
            </div>
            <div className="modal-backdrop" onClick={closeChat} />
          </div>
        );
      })()}
    </div>
  );
}

export default VideoAnalysis;
